import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useHoverCapable } from "@/hooks/useHoverCapable";
import { merosZustandLabel } from "@/lib/meros";
import type { PlanungAssignment, PlanungRow } from "@/lib/montageplanung";
import { AssignmentTooltip } from "./AssignmentTooltip";
import { assignmentBarLabel } from "./displayLabels";
import {
  MP_LONG_PRESS_DRAG_MS,
  MP_LONG_PRESS_MOVE_TOLERANCE,
} from "./layoutConstants";

export interface BarGeom {
  startIdx: number;
  span: number;
  clipLeft: boolean;
  clipRight: boolean;
}

export type AssignmentDragMode = "move" | "resize-start" | "resize-end";

export interface AssignmentLinkPickState {
  primaryId: number;
  selectedIds: number[];
  excludedIds: number[];
  active: boolean;
}

type DragPointerEvent = React.MouseEvent | React.PointerEvent;

export interface AssignmentBarProps {
  assignment: PlanungAssignment;
  row: PlanungRow;
  geom: BarGeom;
  canEdit: boolean;
  dayWidth: number;
  barHeight: number;
  barTop: number;
  isDragging: boolean;
  isLinkDragging?: boolean;
  linkHighlight?: boolean;
  planFocusHighlight?: boolean;
  linkPickState?: AssignmentLinkPickState;
  onLinkPickToggle?: () => void;
  onLinkedHoverStart?: () => void;
  onLinkedHoverEnd?: () => void;
  onEdit: () => void;
  onView?: () => void;
  /** Mobil: zuerst Lesemodal; Verschieben per Long-Press. */
  preferViewOnTap?: boolean;
  onDragStart: (mode: AssignmentDragMode, event: DragPointerEvent) => void;
  onContextMenu?: (event: React.MouseEvent) => void;
}

export function AssignmentBar({
  assignment,
  geom,
  canEdit,
  dayWidth,
  barHeight,
  barTop,
  isDragging,
  isLinkDragging = false,
  linkHighlight = false,
  planFocusHighlight = false,
  linkPickState,
  onLinkPickToggle,
  onLinkedHoverStart,
  onLinkedHoverEnd,
  onEdit,
  onView,
  preferViewOnTap = false,
  onDragStart,
  onContextMenu,
}: AssignmentBarProps) {
  const inset = geom.span === 1 ? 1 : 2;
  const width = geom.span * dayWidth - inset * 2;
  const left = geom.startIdx * dayWidth + inset;
  const barLabel = assignmentBarLabel(assignment.title, geom.span, width);
  const isSingleDay = geom.span === 1;
  const linkedCount = assignment.linked_assignment_ids?.length ?? 0;
  const isSeries = !!assignment.is_series && linkedCount > 0;
  const showSeriesLabel = isSeries && width >= 78;
  const containerRef = useRef<HTMLDivElement>(null);
  const barButtonRef = useRef<HTMLButtonElement>(null);
  const hoverCapable = useHoverCapable();
  const [hovered, setHovered] = useState(false);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [longPressHolding, setLongPressHolding] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const longPressCleanupRef = useRef<(() => void) | null>(null);

  const isLinkPickPrimary =
    linkPickState?.active && linkPickState.primaryId === assignment.id;
  const isLinkPickExcluded =
    linkPickState?.active && linkPickState.excludedIds.includes(assignment.id);
  const isLinkPickSelectable =
    linkPickState?.active && !isLinkPickPrimary && !isLinkPickExcluded;
  const isLinkPickSelected =
    isLinkPickSelectable && linkPickState.selectedIds.includes(assignment.id);
  const pickModeBlocksEdit = isLinkPickSelectable || isLinkPickPrimary;
  const longPressMoveEnabled =
    preferViewOnTap && canEdit && !pickModeBlocksEdit && !hoverCapable;

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current != null) {
        window.clearTimeout(longPressTimerRef.current);
      }
      longPressCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!longPressHolding) return;
    document.body.classList.add("mp-long-press-holding");
    window.getSelection()?.removeAllRanges();
    return () => document.body.classList.remove("mp-long-press-holding");
  }, [longPressHolding]);

  useEffect(() => {
    if (!longPressMoveEnabled) return;
    const el = barButtonRef.current;
    if (!el) return;
    const preventSelect = (e: Event) => e.preventDefault();
    el.addEventListener("selectstart", preventSelect);
    return () => el.removeEventListener("selectstart", preventSelect);
  }, [longPressMoveEnabled]);

  function clearLongPressTimer() {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setLongPressHolding(false);
    longPressStartRef.current = null;
    longPressCleanupRef.current?.();
    longPressCleanupRef.current = null;
  }

  function handleMouseEnter(e: React.MouseEvent) {
    if (!hoverCapable) return;
    setHovered(true);
    setPointer({ x: e.clientX, y: e.clientY });
    if (linkedCount > 0) onLinkedHoverStart?.();
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!hoverCapable || !hovered) return;
    setPointer({ x: e.clientX, y: e.clientY });
  }

  function handleMouseLeave() {
    if (!hoverCapable) return;
    setHovered(false);
    setPointer(null);
    if (linkedCount > 0) onLinkedHoverEnd?.();
  }

  function handleBarPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (!longPressMoveEnabled) return;
    if (e.pointerType === "mouse") return;

    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    window.getSelection()?.removeAllRanges();
    clearLongPressTimer();

    const startX = e.clientX;
    const startY = e.clientY;
    longPressStartRef.current = { x: startX, y: startY };
    setLongPressHolding(true);

    const onPointerMove = (ev: PointerEvent) => {
      if (!longPressStartRef.current) return;
      const dx = ev.clientX - longPressStartRef.current.x;
      const dy = ev.clientY - longPressStartRef.current.y;
      if (Math.hypot(dx, dy) > MP_LONG_PRESS_MOVE_TOLERANCE) {
        clearLongPressTimer();
      }
    };

    const onPointerEnd = () => {
      clearLongPressTimer();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
    longPressCleanupRef.current = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };

    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null;
      longPressStartRef.current = null;
      setLongPressHolding(false);
      window.getSelection()?.removeAllRanges();
      suppressClickRef.current = true;
      longPressCleanupRef.current?.();
      longPressCleanupRef.current = null;
      onDragStart("move", e);
    }, MP_LONG_PRESS_DRAG_MS);
  }

  return (
    <>
      <div
        ref={containerRef}
        data-mp-assignment-id={assignment.id}
        className={clsx(
          "mp-bar-container",
          (isDragging || isLinkDragging) && "mp-bar-container-dragging",
          longPressHolding && "mp-bar-container-long-press",
          isLinkDragging && "mp-bar-container-link-drag",
          linkHighlight && "mp-bar-container-link-highlight",
          planFocusHighlight && "mp-bar-container-plan-focus",
          isLinkPickSelectable && "mp-bar-container-link-pick",
          isLinkPickSelected && "mp-bar-container-link-pick-selected",
          isLinkPickPrimary && "mp-bar-container-link-pick-primary",
        )}
        style={{ left, width, top: barTop, height: barHeight }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onPointerDown={hoverCapable ? undefined : () => setHovered(false)}
        onContextMenu={
          onContextMenu
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onContextMenu(e);
              }
            : undefined
        }
      >
        {canEdit && !geom.clipLeft && !pickModeBlocksEdit && hoverCapable && (
          <div
            className="mp-bar-resize mp-bar-resize-start"
            title="Start verschieben"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDragStart("resize-start", e);
            }}
          />
        )}
        <button
          ref={barButtonRef}
          type="button"
          className={clsx(
            "mp-bar",
            !canEdit && "mp-bar-readonly",
            assignment.is_fixed && "mp-bar-fixed",
            assignment.is_blocker && "mp-bar-blocker",
            assignment.monteur_proposal_status === "pending" && "mp-bar-monteur-proposal",
            assignment.is_company_event && "mp-bar-company-event",
            geom.clipLeft && "mp-bar-clip-left",
            geom.clipRight && "mp-bar-clip-right",
            isSingleDay && "mp-bar-single-day",
            barLabel !== assignment.title && "mp-bar-abbrev",
            linkHighlight && "mp-bar-link-highlight",
            planFocusHighlight && "mp-bar-plan-focus",
            isLinkPickSelectable && "mp-bar-link-pick-target",
            isLinkPickSelected && "mp-bar-link-pick-selected",
            isLinkPickPrimary && "mp-bar-link-pick-primary",
            longPressHolding && "mp-bar-long-press-holding",
          )}
          style={{
            backgroundColor: assignment.is_company_event
              ? undefined
              : assignment.color || "#0a84ff",
          }}
          title={
            longPressMoveEnabled
              ? `${assignment.title}${assignment.is_company_event ? " (Betriebsveranstaltung)" : ""} – tippen für Details, 1 s halten zum Verschieben`
              : isLinkPickSelectable
                ? `${assignment.title} – zum Verknüpfen auswählen`
                : assignment.is_company_event
                  ? `${assignment.title} (Betriebsveranstaltung)`
                  : linkedCount > 0 && canEdit
                    ? `${assignment.title} – Umschalt halten, um nur diesen Einsatz zu verschieben`
                    : assignment.title
          }
          aria-label={assignment.title}
          onPointerDown={handleBarPointerDown}
          onMouseDown={(e) => {
            if (!canEdit || pickModeBlocksEdit || !hoverCapable) return;
            e.stopPropagation();
            e.preventDefault();
            onDragStart("move", e);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setHovered(false);
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            if (isLinkPickSelectable) {
              onLinkPickToggle?.();
              return;
            }
            if (preferViewOnTap && onView) {
              onView();
              return;
            }
            if (canEdit && !pickModeBlocksEdit) onEdit();
            else onView?.();
          }}
        >
          <span className="mp-bar-label">{barLabel}</span>
          {assignment.meros_abweichung ? (
            <span
              className="mp-bar-meros-badge"
              title={
                assignment.meros_ist && assignment.meros_soll
                  ? `Eingetragener Stand ${merosZustandLabel(assignment.meros_ist)} passt nicht zu ${merosZustandLabel(assignment.meros_soll)}.`
                  : "Der eingetragene Stand passt nicht zu diesem Termin."
              }
            >
              {geom.span <= 1 ? "!" : "Passt nicht"}
            </span>
          ) : null}
          {linkedCount > 0 && (
            <span
              className={isSeries ? "mp-bar-series-badge" : "mp-bar-link-badge"}
              title={
                isSeries
                  ? `Serie · ${linkedCount + 1} Termine. Projekte gelten nur für diesen Einsatz. Umschalt halten, um nur diesen Termin zu verschieben.`
                  : `Verknüpft mit ${linkedCount} ${linkedCount === 1 ? "weiterem Einsatz" : "weiteren Einsätzen"}. Umschalt halten, um nur diesen Einsatz zu verschieben.`
              }
              aria-hidden
            >
              {showSeriesLabel ? "Serie" : isSeries ? "S" : "⛓"}
            </span>
          )}
        </button>
        {canEdit && !geom.clipRight && !pickModeBlocksEdit && hoverCapable && (
          <div
            className="mp-bar-resize mp-bar-resize-end"
            title="Ende verlängern/verkürzen"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDragStart("resize-end", e);
            }}
          />
        )}
      </div>

      <AssignmentTooltip
        assignment={assignment}
        anchorRef={containerRef}
        pointerX={pointer?.x}
        visible={hoverCapable && hovered && !pickModeBlocksEdit}
        disabled={isDragging || isLinkDragging}
      />
    </>
  );
}
